import { useEffect, useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getSettings, updateSettings } from '@/api/settings';
import { useAuthStore } from '@/store/auth';
import { Button } from './Button';

export function TelegramPrSettings() {
  const admin = useAuthStore(s => s.admin);
  if (admin?.role !== 'super_admin') return null;
  return <PrSettingsForm />;
}

function PrSettingsForm() {
  const queryClient = useQueryClient();
  const { data: settings } = useQuery({ queryKey: ['settings'], queryFn: getSettings });
  const [enabled, setEnabled] = useState(false);
  const [token, setToken] = useState('');
  const [removeToken, setRemoveToken] = useState(false);
  const [reviewToken, setReviewToken] = useState('');
  const [removeReviewToken, setRemoveReviewToken] = useState(false);
  const [repos, setRepos] = useState('');
  const [chat, setChat] = useState('');
  const [thread, setThread] = useState('');
  const [method, setMethod] = useState('squash');
  const [saved, setSaved] = useState(false);
  useEffect(() => {
    if (!settings) return;
    setEnabled(settings.pr_enabled === '1');
    setRepos(settings.pr_github_repos || '');
    setChat(settings.pr_telegram_chat_id || '');
    setThread(settings.pr_telegram_thread_id || '');
    setMethod(settings.pr_merge_method || 'squash');
  }, [settings]);
  const mutation = useMutation({
    mutationFn: updateSettings,
    onSuccess: async () => {
      setToken(''); setRemoveToken(false); setReviewToken(''); setRemoveReviewToken(false); setSaved(true);
      await queryClient.invalidateQueries({ queryKey: ['settings'] });
    },
  });
  const submit = (event: FormEvent) => {
    event.preventDefault(); setSaved(false);
    const data: Record<string, string> = {
      pr_enabled: enabled ? '1' : '0', pr_github_repos: repos.trim(),
      pr_telegram_chat_id: chat.trim(), pr_telegram_thread_id: thread.trim(), pr_merge_method: method,
    };
    if (removeToken) data.pr_github_token = '';
    else if (token.trim()) data.pr_github_token = token.trim();
    if (removeReviewToken) data.pr_review_token = '';
    else if (reviewToken.trim()) data.pr_review_token = reviewToken.trim();
    mutation.mutate(data);
  };
  const fieldClass = 'block w-full px-3 py-2 border border-border-strong rounded-md bg-surface text-content sm:text-sm';
  return (
    <form onSubmit={submit} onChange={() => setSaved(false)} className="bg-surface border border-border rounded-lg mt-6">
      <div className="px-6 py-4 border-b border-border">
        <h2 className="text-lg font-medium text-content">GitHub PR → Telegram</h2>
        <p className="text-sm text-content-subtle mt-1">Одна кнопка в Telegram отправляет GitHub approve и выполняет мердж PR.</p>
      </div>
      <div className="px-6 py-5 space-y-4">
        <label className="flex items-center gap-2 text-sm text-content">
          <input type="checkbox" checked={enabled} onChange={e => setEnabled(e.target.checked)} />
          Включить уведомления о PR и мердж в Telegram
        </label>
        <div>
          <label htmlFor="pr-token" className="block text-sm text-content-muted mb-1">GitHub token</label>
          <input id="pr-token" type="password" autoComplete="new-password" value={token}
            disabled={removeToken} required={enabled && settings?.pr_github_token_configured !== '1' && !removeToken}
            onChange={e => setToken(e.target.value)} placeholder={settings?.pr_github_token_configured === '1' ? 'Токен сохранён. Оставьте пустым, чтобы сохранить текущий.' : 'github_pat_…'} className={fieldClass} />
          <p className="text-xs text-content-subtle mt-1">Contents: read and write, Pull requests: read and write. Сохранённый токен не возвращается в браузер.</p>
          {settings?.pr_github_token_configured === '1' && <label className="flex items-center gap-2 text-sm text-content-muted mt-2">
            <input type="checkbox" checked={removeToken} onChange={e => { setRemoveToken(e.target.checked); if (e.target.checked) setEnabled(false); }} />Удалить сохранённый токен
          </label>}
        </div>
        <div>
          <label htmlFor="pr-review-token" className="block text-sm text-content-muted mb-1">Токен для approve (необязательно)</label>
          <input id="pr-review-token" type="password" autoComplete="new-password" value={reviewToken} disabled={removeReviewToken}
            onChange={e => setReviewToken(e.target.value)} placeholder={settings?.pr_review_token_configured === '1' ? 'Токен сохранён. Оставьте пустым, чтобы сохранить текущий.' : 'По умолчанию используется основной токен'} className={fieldClass} />
          <p className="text-xs text-content-subtle mt-1">Если PR создаёт владелец основного токена, укажите токен другого аккаунта с правом Pull requests: write. GitHub не разрешает автору одобрять собственный PR.</p>
          {settings?.pr_review_token_configured === '1' && <label className="flex items-center gap-2 text-sm text-content-muted mt-2">
            <input type="checkbox" checked={removeReviewToken} onChange={e => setRemoveReviewToken(e.target.checked)} />Удалить токен для approve и использовать основной
          </label>}
        </div>
        <div>
          <label htmlFor="pr-repos" className="block text-sm text-content-muted mb-1">Репозитории</label>
          <input id="pr-repos" value={repos} onChange={e => setRepos(e.target.value)} placeholder="Все доступные репозитории" className={fieldClass} />
          <p className="text-xs text-content-subtle mt-1">По умолчанию приходят PR из всех доступных токену репозиториев, включая новые. Чтобы ограничить список, укажите owner/repo через запятую.</p>
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label htmlFor="pr-chat" className="block text-sm text-content-muted mb-1">ID чата уведомлений</label>
            <input id="pr-chat" value={chat} required={enabled} pattern="-\d+" onChange={e => setChat(e.target.value)} placeholder="-1001234567890" className={fieldClass} />
          </div>
          <div>
            <label htmlFor="pr-thread" className="block text-sm text-content-muted mb-1">ID темы для PR</label>
            <input id="pr-thread" type="number" min="1" step="1" required={enabled} value={thread} onChange={e => setThread(e.target.value)} placeholder="123" className={fieldClass} />
          </div>
        </div>
        <div>
          <label htmlFor="pr-method" className="block text-sm text-content-muted mb-1">Метод мерджа</label>
          <select id="pr-method" value={method} onChange={e => setMethod(e.target.value)} className={fieldClass}>
            <option value="squash">Squash</option><option value="merge">Merge commit</option><option value="rebase">Rebase</option>
          </select>
        </div>
        <p className="text-xs text-content-subtle">Используется существующий бот уведомлений. Открытые PR проверяются каждые пять минут.</p>
      </div>
      <div className="px-6 py-4 border-t border-border flex items-center justify-between gap-3">
        <div role="status" className="text-sm">
          {saved && <span className="text-success">Настройки сохранены</span>}
          {mutation.isError && <span className="text-danger">{mutation.error instanceof Error ? mutation.error.message : 'Не удалось сохранить'}</span>}
        </div>
        <Button type="submit" loading={mutation.isPending}>Сохранить GitHub → Telegram</Button>
      </div>
    </form>
  );
}
